import _class_call_check from "@swc/helpers/src/_class_call_check.mjs";
import _inherits from "@swc/helpers/src/_inherits.mjs";
import _instanceof from "@swc/helpers/src/_instanceof.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import _wrap_native_super from "@swc/helpers/src/_wrap_native_super.mjs";
import _create_super from "@swc/helpers/src/_create_super.mjs";
// ignore-string-externalization
/* eslint-disable no-underscore-dangle */ export var ResponseError = /*#__PURE__*/ function(Error1) {
    "use strict";
    _inherits(ResponseError, Error1);
    var _super = _create_super(ResponseError);
    function ResponseError(statusCode) {
        for(var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
            args[_key - 1] = arguments[_key];
        }
        _class_call_check(this, ResponseError);
        var _this;
        _this = _super.call.apply(_super, [
            this,
            statusCode
        ].concat(_to_consumable_array(args)));
        _this.name = "ResponseError";
        _this.statusCode = statusCode;
        return _this;
    }
    return ResponseError;
}(_wrap_native_super(Error));
export var verifyResponse = function(response) {
    if (response.ok) {
        return response;
    }
    throw new ResponseError(response.status);
};
export var toJSON = function(response) {
    return response.text().then(function(text) {
        try {
            return JSON.parse(text);
        } catch (e) {
            return text;
        }
    });
};
/**
 * API helper
 *
 * @module API
 * @param {Object} config A configuration object
 * @param {String} config.baseUrl The base URL for making requests, it will be prepended to the final URL parameter
 * @param {String} config.defaultHeaders A set of default headers that will be included with each request.
 * @param {Function} config.getAccessToken The function to call for getting an access token
 * @param {Function} config.mapResponse A function to process the `Response` object
 * @param {Function} config.getCacheKey A function that return a cache key based on the outgoing request (defaults to return the request's URI)
 * @returns {API} instance
 *
 * @example
 * // basic usage
 * const accessTokenManager = new AccessTokenManager({
 *   url: `https://generic.wg.spotify.com/creator-auth-proxy/v1/web/token?client_id=${CLIENT_ID}`,
 *   options: { credentials: 'include' },
 * });
 *
 * const api = new API({ getAccessToken: accessTokenManager.get });
 * await api.get('https://api.spotify.com/v1/me');
 *
 * @example
 * // advanced usage
 * const api = new API({
 *   baseUrl: 'https://api.spotify.com',
 *   defaultHeaders: { 'Spotify-App-Version': '1.0.0', 'App-Platform': 'Browser' },
 *   getAccessToken: async () => 'some-access-token',
 *   mapResponse: () => console.log,
 * });
 * await api.get('/v1/me');
 */ export var API = function API() {
    "use strict";
    var ref = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, _baseUrl = ref.baseUrl, baseUrl = _baseUrl === void 0 ? "" : _baseUrl, _defaultHeaders = ref.defaultHeaders, defaultHeaders = _defaultHeaders === void 0 ? {
        Accept: "application/json",
        "Content-Type": "application/json"
    } : _defaultHeaders, _getAccessToken = ref.getAccessToken, getAccessToken = _getAccessToken === void 0 ? function() {
        return Promise.resolve();
    } : _getAccessToken, _mapResponse = ref.mapResponse, mapResponse = _mapResponse === void 0 ? function(response) {
        return response;
    } : _mapResponse, _getCacheKey = ref.getCacheKey, getCacheKey = _getCacheKey === void 0 ? function(requestConfig) {
        return requestConfig.url;
    } : _getCacheKey;
    var _this = this;
    _class_call_check(this, API);
    /**
   * Main method for performing `fetch` requests
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the `fetch` API
   * @return {Promise} `fetch` response
   */ this.request = function(url) {
        var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return _this._getAccessToken().then(function(token) {
            var tokenHeaders = {};
            if (token) {
                tokenHeaders.Authorization = "Bearer ".concat(token);
            }
            var headers = new Headers(_object_spread({}, _this._defaultHeaders, tokenHeaders, options.headers));
            var isContentTypeJSON = headers.get("Content-Type") === "application/json";
            var body = typeof options.body === "object" && isContentTypeJSON ? JSON.stringify(options.body) : options.body;
            return {
                url: _this._baseUrl + url,
                options: _object_spread_props(_object_spread({}, options), {
                    headers: headers,
                    body: body
                })
            };
        }).then(function(requestConfig) {
            if (requestConfig.options.method === "GET") {
                var cacheKey = _this._getCacheKey(requestConfig);
                if (!_this._getCache.has(cacheKey)) {
                    var _this1 = _this;
                    var promise = fetch(requestConfig.url, requestConfig.options).then(function(response) {
                        return response.text().then(function(data) {
                            return {
                                data: data,
                                response: response
                            };
                        });
                    }).catch(function(error) {
                        _this1._getCache.delete(cacheKey);
                        throw error;
                    }).then(function(payload) {
                        _this1._getCache.delete(cacheKey);
                        return payload;
                    });
                    _this._getCache.set(cacheKey, promise);
                }
                return _this._getCache.get(cacheKey)// if data is empty then we use null for body.
                // @see https://ghe.spotify.net/creator/web-shared/issues/4
                .then(function(param) {
                    var data = param.data, response = param.response;
                    return new Response(data || null, response);
                });
            }
            return fetch(requestConfig.url, requestConfig.options);
        }).catch(function(error) {
            // thanks for nothing Microsoft Edge...
            // @see https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8653298/
            if (_instanceof(error, window.DOMException) && error.code === 17) {
                return new Response(null, {
                    status: 401
                });
            }
            throw error;
        }).then(_this._mapResponse);
    };
    /**
   * Shorthand method for performing a GET `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */ this.get = function(url) {
        var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return _this.request(url, _object_spread_props(_object_spread({}, options), {
            method: "GET"
        }));
    };
    /**
   *
   * Shorthand method for performing a POST `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */ this.post = function(url) {
        var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return _this.request(url, _object_spread_props(_object_spread({}, options), {
            method: "POST"
        }));
    };
    /**
   * Shorthand method for performing a PATCH `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */ this.patch = function(url) {
        var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return _this.request(url, _object_spread_props(_object_spread({}, options), {
            method: "PATCH"
        }));
    };
    /**
   * Shorthand method for performing a PUT `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */ this.put = function(url) {
        var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return _this.request(url, _object_spread_props(_object_spread({}, options), {
            method: "PUT"
        }));
    };
    /**
   * Shorthand method for performing a DELETE `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */ this.delete = function(url) {
        var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return _this.request(url, _object_spread_props(_object_spread({}, options), {
            method: "DELETE"
        }));
    };
    this._baseUrl = baseUrl;
    this._defaultHeaders = defaultHeaders;
    this._getAccessToken = getAccessToken;
    this._mapResponse = mapResponse;
    this._getCacheKey = getCacheKey;
    this._getCache = new Map();
};
